import { useState } from 'react';
import type { SeatingReviewResponse } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';
import type { SeatingReport } from '@league/shared/seating';
import { Button } from '../../components/ui';
import { apiPost } from '../../lib/api';
export type { SeatingReport, SeatingSwap } from '@league/shared/seating';

type Tables = { tableNumber: number; seats: { seat: string; enrollmentId: string }[] }[];
type Request = NonNullable<SeatingReport['activeRequests']>[number];
function unmetMembers(request: Request, tables: Tables) {
  const tableOf = new Map(
    tables
      .filter((t) => t.seats.length >= 3 && t.seats.length <= 4)
      .flatMap((t) => t.seats.map((p) => [p.enrollmentId, t.tableNumber] as const)),
  );
  if (request.separatePairs) {
    const counts = tables
      .filter((t) => t.seats.length >= 3 && t.seats.length <= 4)
      .map((t) => t.seats.filter((p) => request.playerIds.includes(p.enrollmentId)).length);
    return counts.filter((n) => n === 2).length === 2 ? 0 : 1;
  }
  if (request.wholeGroup)
    return request.playerIds.every((id) => tableOf.has(id)) &&
      new Set(request.playerIds.map((id) => tableOf.get(id))).size === 1
      ? 0
      : 1;
  return request.playerIds.filter(
    (id) =>
      !tableOf.has(id) ||
      !request.playerIds.some((other) => other !== id && tableOf.get(other) === tableOf.get(id)),
  ).length;
}

const met = (request: Request, tables: Tables) => unmetMembers(request, tables) === 0;

/** Compact information about the current arrangement, using the existing review evidence. */
export function ConstraintPanel({
  sessionId,
  tables,
  nameOf,
  canApplySwap,
  onApplySwap,
}: {
  sessionId: string;
  tables: Tables;
  nameOf: (id: string) => string;
  canApplySwap: (a: string, b: string) => boolean;
  onApplySwap: (a: string, b: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [expandedTables, setExpandedTables] = useState<Set<number>>(() => new Set());
  const review = useQuery({
    queryKey: ['seating-suggestions', sessionId, tables],
    queryFn: () =>
      apiPost<SeatingReviewResponse>(`/api/sessions/${sessionId}/seating/review`, {
        tables,
        notes: [],
      }),
    staleTime: 0,
  });
  if (review.isFetching || review.isError || !review.data) return null;
  const proposed = review.data.swap;
  const swap =
    proposed &&
    ['friends', 'pace', 'rating', 'variety'].includes(proposed.reason) &&
    canApplySwap(proposed.a, proposed.b)
      ? proposed
      : null;
  const seen = new Set<string>();
  const requests = (review.data.report.activeRequests ?? [])
    .map((r) => ({ ...r, playerIds: [...new Set(r.playerIds)] }))
    .filter((r) => {
      const key = JSON.stringify([r.wholeGroup, r.separatePairs, [...r.playerIds].sort()]);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  const unmet = requests.filter((r) => !met(r, tables));

  const after = swap
    ? tables.map((t) => ({
        ...t,
        seats: t.seats.map((p) => ({
          ...p,
          enrollmentId:
            p.enrollmentId === swap.a
              ? swap.b
              : p.enrollmentId === swap.b
                ? swap.a
                : p.enrollmentId,
        })),
      }))
    : tables;
  const lost = requests.filter((r) => unmetMembers(r, after) > unmetMembers(r, tables));
  const names = (r: Request) => r.playerIds.map(nameOf).join(', ');
  const helped = requests.filter((r) => unmetMembers(r, after) < unmetMembers(r, tables));
  const benefit =
    swap?.reason === 'friends'
      ? helped.length
        ? `to help honor Play Together for ${helped.map(names).join('; ')}`
        : 'to help honor an active Play Together request'
      : swap?.reason === 'pace'
        ? 'for a more comfortable flow of play'
        : swap?.reason === 'rating'
          ? 'to bring together players with similar performance records'
          : 'to give players different tablemates';
  return (
    <details
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      className="rounded-md border border-light bg-paper p-5"
    >
      <summary className="cursor-pointer rounded-sm text-[13px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current">
        A look at your tables
      </summary>
      <div className="mt-4 flex flex-col gap-3 text-[13px]">
        <section>
          <h3 className="font-semibold">Play Together requests</h3>
          <p>
            {requests.length
              ? `${requests.length - unmet.length} of ${requests.length} groups accommodated.`
              : 'No requests for this session.'}
          </p>
          {unmet.map((r) => (
            <p key={r.playerIds.join('|')} className="mt-2">
              Review the Play Together placement for {names(r)}.{' '}
              {r.separatePairs
                ? 'These four friends need two pairs at separate tables.'
                : r.wholeGroup
                  ? 'This request keeps the group at one playable table.'
                  : 'Each attending member needs at least one companion from this group at a playable table.'}
            </p>
          ))}
        </section>
        <section>
          <h3 className="font-semibold">Tablemate variety</h3>
          {!review.data.report.tableRepeats?.length ? (
            <p>No repeated full tables or trios.</p>
          ) : (
            [...new Set(review.data.report.tableRepeats.map((r) => r.tableNumber))].map(
              (number) => (
                <details
                  key={number}
                  open={expandedTables.has(number)}
                  onToggle={(event) => {
                    const open = event.currentTarget.open;
                    setExpandedTables((current) => {
                      const next = new Set(current);
                      if (open) next.add(number);
                      else next.delete(number);
                      return next;
                    });
                  }}
                  className="mt-2 rounded border border-light p-3"
                >
                  <summary className="min-h-9 cursor-pointer font-medium focus-visible:outline focus-visible:outline-2">
                    Table {number}
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {review.data.report
                      .tableRepeats!.filter((r) => r.tableNumber === number)
                      .map((r) => (
                        <li
                          key={`${r.sessionId}:${r.previousTableNumber}:${r.playerIds.join('|')}`}
                        >
                          <span className="font-medium">
                            {r.kind === 'full' ? 'All four players' : 'Three players'} from Session{' '}
                            {r.sessionNumber} are together again.
                          </span>{' '}
                          {r.playerIds.map(nameOf).join(', ')}.
                          {r.intentional ? ' Intentional — Play Together every session.' : ''}
                        </li>
                      ))}
                  </ul>
                </details>
              ),
            )
          )}
        </section>
        {swap ? (
          <section className="flex flex-col gap-3">
            <h3 className="font-semibold">Suggested change</h3>
            <p>
              Swap {nameOf(swap.a)} and {nameOf(swap.b)} {benefit}.
            </p>
            {lost.map((r) => (
              <p key={r.playerIds.join('|')}>
                {met(r, tables)
                  ? 'This would break the currently met Play Together preference for'
                  : 'This would leave more players apart from their Play Together group for'}{' '}
                {names(r)}. Review that tradeoff before swapping.
              </p>
            ))}
            {swap.tradeoffs
              ?.filter((message) => !lost.length || !message.startsWith('This would leave '))
              .map((message) => (
                <p key={message}>{message}</p>
              ))}
            <Button
              variant="secondary"
              size="inline"
              className="h-auto min-h-11 max-w-full self-start whitespace-normal px-3 py-2 text-left"
              onClick={() => {
                if (canApplySwap(swap.a, swap.b)) onApplySwap(swap.a, swap.b);
              }}
            >
              Swap {nameOf(swap.a)} and {nameOf(swap.b)}
            </Button>
          </section>
        ) : null}
      </div>
    </details>
  );
}
